import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import {
  DocumentClassificationTestResponse,
  DocumentClassificationSuggestionDto,
  DocumentExtractionPreviewResponse,
  DocumentExtractionSuggestedFieldDto,
  DocumentExtractionTemplateDto,
  DocumentLabelSetDto,
  DocumentSummarisationPreviewResponse,
  DocumentSummarisationTemplateDto,
  documentHubService,
} from '../../../services/documentHubService';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

type HubTab = 'classification' | 'summarisation' | 'extraction';

const duplicateDisplayName = (name: string, maxLength: number): string => {
  const suffix = '_1';
  const trimmed = name.trim();
  const duplicated = `${trimmed}${suffix}`;
  if (duplicated.length <= maxLength) {
    return duplicated;
  }
  return `${trimmed.slice(0, Math.max(0, maxLength - suffix.length))}${suffix}`;
};

const deriveClassificationLabelFromSetName = (labelSetName: string): string =>
  labelSetName.trim().replace(/\s+Label Set$/i, '').trim();

const isSpreadsheetTrainingFile = (file: File | null | undefined): boolean =>
  !!file && /\.xlsx$/i.test(file.name);

const isPdfTrainingFile = (file: File | null | undefined): boolean =>
  !!file && /\.pdf$/i.test(file.name);

type TrainerField = {
  id: string;
  fieldName: string;
  exampleValue: string;
  boundingBoxJson?: string;
  pageNumber?: number;
};

type PersistentHighlight = {
  id: string;
  color: string;
  selectedText: string;
  rects: Array<{ left: number; top: number; width: number; height: number }>;
};

type SpreadsheetPreviewSheet = {
  name: string;
  rows: string[][];
  headerRowIndex: number;
  columnHeaders: Record<number, string>;
};

type SpreadsheetCellCapture = {
  sheetName: string;
  rowIndex: number;
  colIndex: number;
  rowLabel: string;
  colHeader: string;
  value: string;
};

const MONTH_HEADER_PATTERN =
  /\b(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*[-\s/]?\d{2,4}\b/i;

const MONTH_ABBREV_PATTERN = /^[A-Za-z]{3}-\d{2}$/;

const isSpreadsheetMonthLabel = (value: string): boolean => {
  const trimmed = value.trim();
  if (!trimmed) {
    return false;
  }
  return MONTH_HEADER_PATTERN.test(trimmed) || MONTH_ABBREV_PATTERN.test(trimmed);
};

const tryFormatExcelSerialAsMonth = (value: string): string | null => {
  if (!/^\d{4,5}$/.test(value.trim())) {
    return null;
  }

  const serial = Number.parseInt(value.trim(), 10);
  if (serial < 39000 || serial > 65000) {
    return null;
  }

  const date = new Date(Date.UTC(1899, 11, 30 + serial));
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  const month = date.toLocaleString('en-GB', { month: 'short', timeZone: 'UTC' });
  const year = String(date.getUTCFullYear()).slice(-2);
  return `${month}-${year}`;
};

const parseMonthLabel = (label: string): Date | null => {
  const trimmed = label.trim();
  if (!trimmed) {
    return null;
  }

  const abbrevMatch = trimmed.match(/^([A-Za-z]{3})-(\d{2})$/);
  if (abbrevMatch) {
    const monthIndex = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'].indexOf(
      abbrevMatch[1].toLowerCase()
    );
    if (monthIndex >= 0) {
      const year = 2000 + Number.parseInt(abbrevMatch[2], 10);
      return new Date(Date.UTC(year, monthIndex, 1));
    }
  }

  const namedMatch = trimmed.match(
    /\b(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*[-\s/]?(\d{2,4})\b/i
  );
  if (namedMatch) {
    const monthIndex = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'].indexOf(
      namedMatch[1].slice(0, 3).toLowerCase()
    );
    if (monthIndex >= 0) {
      const yearPart = namedMatch[2];
      const year = yearPart.length === 2 ? 2000 + Number.parseInt(yearPart, 10) : Number.parseInt(yearPart, 10);
      return new Date(Date.UTC(year, monthIndex, 1));
    }
  }

  return null;
};

const formatMonthLabel = (date: Date): string => {
  const month = date.toLocaleString('en-GB', { month: 'short', timeZone: 'UTC' });
  const year = String(date.getUTCFullYear()).slice(-2);
  return `${month}-${year}`;
};

const addMonthsUtc = (date: Date, offset: number): Date =>
  new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + offset, 1));

const normalizeSpreadsheetHeaderCell = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }
  if (isSpreadsheetMonthLabel(trimmed)) {
    return trimmed;
  }
  return tryFormatExcelSerialAsMonth(trimmed) ?? trimmed;
};

const detectSpreadsheetHeaderRowIndex = (rows: string[][]): number => {
  let bestIndex = 0;
  let bestMonthCount = 0;

  for (let rowIndex = 0; rowIndex < Math.min(rows.length, 25); rowIndex++) {
    let monthCount = 0;
    for (const cell of rows[rowIndex] ?? []) {
      const normalized = normalizeSpreadsheetHeaderCell(cell ?? '');
      if (isSpreadsheetMonthLabel(normalized)) {
        monthCount++;
      }
    }
    if (monthCount > bestMonthCount) {
      bestMonthCount = monthCount;
      bestIndex = rowIndex;
    }
  }

  if (bestMonthCount > 0) {
    return bestIndex;
  }

  let bestScore = -1;
  for (let rowIndex = 0; rowIndex < Math.min(rows.length, 20); rowIndex++) {
    const row = rows[rowIndex];
    let score = 0;

    for (let colIndex = 1; colIndex < row.length; colIndex++) {
      const cell = row[colIndex]?.trim() ?? '';
      if (!cell) {
        continue;
      }
      if (/^(?:total|less|due|management)/i.test(cell)) {
        score -= 2;
      } else if (cell.length <= 16) {
        score += 1;
      }
    }

    if (score > bestScore) {
      bestScore = score;
      bestIndex = rowIndex;
    }
  }

  return bestIndex;
};

const collectSpreadsheetDataColumns = (rows: string[][]): number[] => {
  const dataCols = new Set<number>();

  for (const row of rows) {
    const rowLabel = row[0]?.trim() ?? '';
    if (!rowLabel || !/(unit|meter|kb|apt|flat|shop)/i.test(rowLabel)) {
      continue;
    }

    row.forEach((cell, colIndex) => {
      if (colIndex > 0 && /^-?\d+(\.\d+)?$/.test(cell?.trim() ?? '')) {
        dataCols.add(colIndex);
      }
    });
  }

  return Array.from(dataCols).sort((a, b) => a - b);
};

const buildSpreadsheetColumnHeaders = (rows: string[][]): Record<number, string> => {
  const headers: Record<number, string> = {};

  for (let rowIndex = 0; rowIndex < Math.min(rows.length, 30); rowIndex++) {
    const row = rows[rowIndex] ?? [];
    for (let colIndex = 0; colIndex < row.length; colIndex++) {
      const normalized = normalizeSpreadsheetHeaderCell(row[colIndex] ?? '');
      if (!normalized || headers[colIndex]) {
        continue;
      }
      if (isSpreadsheetMonthLabel(normalized)) {
        headers[colIndex] = normalized;
      }
    }
  }

  const headerRowIndex = detectSpreadsheetHeaderRowIndex(rows);
  const headerRow = rows[headerRowIndex] ?? [];
  for (let colIndex = 0; colIndex < headerRow.length; colIndex++) {
    if (headers[colIndex]) {
      continue;
    }
    const normalized = normalizeSpreadsheetHeaderCell(headerRow[colIndex] ?? '');
    if (normalized && colIndex > 0 && normalized.length <= 24) {
      headers[colIndex] = normalized;
    }
  }

  return fillSpreadsheetMonthColumnGaps(rows, headers);
};

const fillSpreadsheetMonthColumnGaps = (
  rows: string[][],
  headers: Record<number, string>
): Record<number, string> => {
  const filled: Record<number, string> = { ...headers };

  const monthCols = Object.entries(filled)
    .map(([colIndex, label]) => ({ colIndex: Number(colIndex), label }))
    .filter((entry) => isSpreadsheetMonthLabel(entry.label))
    .sort((a, b) => a.colIndex - b.colIndex);

  if (monthCols.length === 0) {
    return filled;
  }

  const dataCols = collectSpreadsheetDataColumns(rows);

  const monthStepBetween = (leftLabel: string, rightLabel: string): number => {
    const leftDate = parseMonthLabel(leftLabel);
    const rightDate = parseMonthLabel(rightLabel);
    if (!leftDate || !rightDate) {
      return -1;
    }
    return leftDate < rightDate ? 1 : -1;
  };

  if (monthCols.length === 1 && dataCols.length > 1) {
    const anchor = monthCols[0];
    const anchorDate = parseMonthLabel(anchor.label);
    if (anchorDate) {
      const orderedDataCols = dataCols.sort((a, b) => a - b);
      const anchorPos = orderedDataCols.indexOf(anchor.colIndex);
      const anchorIndex = anchorPos >= 0 ? anchorPos : 0;
      const step = -1;

      for (let index = 0; index < orderedDataCols.length; index++) {
        const colIndex = orderedDataCols[index];
        if (filled[colIndex]) {
          continue;
        }
        filled[colIndex] = formatMonthLabel(addMonthsUtc(anchorDate, (index - anchorIndex) * step));
      }
    }
  }

  for (let index = 0; index < monthCols.length - 1; index++) {
    const left = monthCols[index];
    const right = monthCols[index + 1];
    const leftDate = parseMonthLabel(left.label);
    if (!leftDate) {
      continue;
    }
    const step = monthStepBetween(left.label, right.label);

    for (let colIndex = left.colIndex + 1; colIndex < right.colIndex; colIndex++) {
      if (filled[colIndex]) {
        continue;
      }
      filled[colIndex] = formatMonthLabel(addMonthsUtc(leftDate, (colIndex - left.colIndex) * step));
    }
  }

  if (dataCols.length > 0 && monthCols.length > 0) {
    const firstMonth = monthCols[0];
    const lastMonth = monthCols[monthCols.length - 1];
    const firstMonthDate = parseMonthLabel(firstMonth.label);
    const lastMonthDate = parseMonthLabel(lastMonth.label);
    const leadingStep =
      monthCols.length >= 2 && firstMonthDate && lastMonthDate
        ? monthStepBetween(firstMonth.label, lastMonth.label)
        : -1;

    if (firstMonthDate) {
      for (const colIndex of dataCols) {
        if (filled[colIndex]) {
          continue;
        }
        filled[colIndex] = formatMonthLabel(
          addMonthsUtc(firstMonthDate, (colIndex - firstMonth.colIndex) * leadingStep)
        );
      }
    }
  }

  return filled;
};

const getSpreadsheetRowLabel = (row: string[], rowIndex: number, headerRowIndex: number): string => {
  if (rowIndex === headerRowIndex) {
    return '';
  }
  const primary = row[0]?.trim();
  if (primary) {
    return primary;
  }
  return row.find((cell) => cell?.trim())?.trim() ?? '';
};

const getSpreadsheetColHeader = (
  columnHeaders: Record<number, string>,
  colIndex: number
): string => {
  if (colIndex === 0) {
    return 'label';
  }

  const probes = [colIndex, colIndex - 1, colIndex + 1];
  for (const probe of probes) {
    const header = columnHeaders[probe];
    if (header && isSpreadsheetMonthLabel(header)) {
      return header;
    }
  }

  for (const probe of probes) {
    const header = columnHeaders[probe];
    if (header) {
      return header;
    }
  }

  return `col_${colIndex}`;
};

const isUsableSpreadsheetColHeader = (colHeader: string): boolean =>
  !!colHeader.trim() && colHeader !== 'label' && !colHeader.startsWith('col_');

const slugifySpreadsheetFieldPart = (input: string): string => {
  const trimmed = input.trim();
  if (!trimmed) {
    return '';
  }

  const meterMatch = trimmed.match(/\((\d{6,})\)/);
  const withoutParen = trimmed.replace(/\(\d{6,}\)/g, '').trim();
  let slug = withoutParen
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

  if (!slug && meterMatch) {
    slug = `meter_${meterMatch[1]}`;
  } else if (slug && meterMatch) {
    slug = `${slug}_${meterMatch[1]}`;
  }

  return slug.slice(0, 48);
};

const buildSpreadsheetFieldName = (cell: SpreadsheetCellCapture): string => {
  const rowPart = slugifySpreadsheetFieldPart(cell.rowLabel);
  const colPart = slugifySpreadsheetFieldPart(cell.colHeader);

  if (rowPart && isUsableSpreadsheetColHeader(cell.colHeader) && colPart) {
    return `${rowPart}__${colPart}`;
  }
  if (rowPart && cell.colIndex > 0) {
    return `${rowPart}__c${cell.colIndex}`;
  }
  if (rowPart && cell.value.trim()) {
    return rowPart;
  }
  if (colPart && cell.value.trim() && isUsableSpreadsheetColHeader(cell.colHeader)) {
    return colPart;
  }
  return slugifySpreadsheetFieldPart(cell.value) || 'extracted_field';
};

const readSpreadsheetCellFromElement = (element: Element): SpreadsheetCellCapture | null => {
  if (!(element instanceof HTMLElement) || element.dataset.trainerCell !== 'true') {
    return null;
  }

  return {
    sheetName: element.dataset.sheetName ?? 'Sheet',
    rowIndex: Number.parseInt(element.dataset.rowIndex ?? '0', 10),
    colIndex: Number.parseInt(element.dataset.colIndex ?? '0', 10),
    rowLabel: element.dataset.rowLabel ?? '',
    colHeader: element.dataset.colHeader ?? '',
    value: element.dataset.cellValue ?? element.textContent?.trim() ?? '',
  };
};

const cellCaptureKey = (cell: SpreadsheetCellCapture): string =>
  `${cell.sheetName}|${cell.rowIndex}|${cell.colIndex}`;

const rectsIntersect = (
  a: { left: number; top: number; width: number; height: number },
  b: { left: number; top: number; width: number; height: number }
): boolean =>
  a.left < b.left + b.width &&
  a.left + a.width > b.left &&
  a.top < b.top + b.height &&
  a.top + a.height > b.top;

const collectSpreadsheetCellsFromContainer = (
  container: HTMLElement,
  targetRects: Array<{ left: number; top: number; width: number; height: number }>
): SpreadsheetCellCapture[] => {
  const containerBounds = container.getBoundingClientRect();
  const captured = new Map<string, SpreadsheetCellCapture>();

  for (const element of Array.from(container.querySelectorAll('[data-trainer-cell="true"]'))) {
    const cell = readSpreadsheetCellFromElement(element);
    if (!cell || !cell.value.trim()) {
      continue;
    }

    const spanRect = element.getBoundingClientRect();
    const relativeRect = {
      left: spanRect.left - containerBounds.left + container.scrollLeft,
      top: spanRect.top - containerBounds.top + container.scrollTop,
      width: spanRect.width,
      height: spanRect.height,
    };

    const intersects = targetRects.some((targetRect) => rectsIntersect(relativeRect, targetRect));
    if (!intersects) {
      continue;
    }

    captured.set(cellCaptureKey(cell), cell);
  }

  return Array.from(captured.values()).sort(
    (a, b) =>
      a.sheetName.localeCompare(b.sheetName) || a.rowIndex - b.rowIndex || a.colIndex - b.colIndex
  );
};

const hasSpreadsheetCellContext = (cells: SpreadsheetCellCapture[]): boolean =>
  cells.some((cell) => {
    const hasRow = !!cell.rowLabel.trim() && cell.colIndex > 0;
    const hasCol = isUsableSpreadsheetColHeader(cell.colHeader);
    return hasRow && hasCol && !!cell.value.trim();
  });

const parseSpreadsheetPreviewText = (text: string): SpreadsheetPreviewSheet[] => {
  const sheets: Array<{ name: string; rows: string[][] }> = [];
  let current: { name: string; rows: string[][] } | null = null;

  for (const rawLine of text.split('\n')) {
    const line = rawLine.trim();
    if (!line) {
      continue;
    }

    if (line.startsWith('Sheet:')) {
      current = { name: line.replace(/^Sheet:\s*/i, '').trim() || 'Sheet', rows: [] };
      sheets.push(current);
      continue;
    }

    if (!current) {
      current = { name: 'Sheet', rows: [] };
      sheets.push(current);
    }

    current.rows.push(line.split('|').map((cell) => cell.trim()));
  }

  return sheets.map((sheet) => {
    const headerRowIndex = detectSpreadsheetHeaderRowIndex(sheet.rows);
    return {
      ...sheet,
      headerRowIndex,
      columnHeaders: buildSpreadsheetColumnHeaders(sheet.rows),
    };
  });
};

const HIGHLIGHT_COLORS = ['#fde68a', '#bfdbfe', '#fecdd3', '#bbf7d0', '#ddd6fe', '#fdba74'];
type MobileTrainerView = 'document' | 'fields';
type DragPoint = { x: number; y: number };

const tabClass = (active: boolean) =>
  [
    'rounded-lg border px-3 py-2 text-sm font-medium transition',
    active
      ? 'border-slate-900 bg-slate-900 text-white'
      : 'border-slate-300 bg-white text-slate-700 hover:border-slate-400',
  ].join(' ');

const DocumentHub: React.FC = () => {
  const [activeTab, setActiveTab] = useState<HubTab>('classification');
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);

  const [labelSetName, setLabelSetName] = useState('');
  const [labelSetDescription, setLabelSetDescription] = useState('');
  const [classificationPrompt, setClassificationPrompt] = useState(
    'Classify this document into the closest label based on both text and visual layout.'
  );
  const [classificationFiles, setClassificationFiles] = useState<File[]>([]);
  const [classificationSuggestions, setClassificationSuggestions] = useState<DocumentClassificationSuggestionDto[]>([]);
  const [classificationTestFile, setClassificationTestFile] = useState<File | null>(null);
  const [classificationTestResult, setClassificationTestResult] = useState<DocumentClassificationTestResponse | null>(null);
  const [classificationTestFeedback, setClassificationTestFeedback] = useState<string | null>(null);
  const [editingLabelSetId, setEditingLabelSetId] = useState<number | null>(null);

  const [summarisationName, setSummarisationName] = useState('');
  const [summarisationDescription, setSummarisationDescription] = useState('');
  const [summarisationPrompt, setSummarisationPrompt] = useState(
    'Summarise this document using headings, key decisions, and next actions.'
  );
  const [summarisationTestFile, setSummarisationTestFile] = useState<File | null>(null);
  const [summarisationPreview, setSummarisationPreview] = useState<DocumentSummarisationPreviewResponse | null>(null);
  const [summarisationFeedback, setSummarisationFeedback] = useState<string | null>(null);
  const [editingSummarisationTemplateId, setEditingSummarisationTemplateId] = useState<number | null>(null);

  const [extractionTemplateName, setExtractionTemplateName] = useState('');
  const [extractionTemplateDescription, setExtractionTemplateDescription] = useState('');
  const [editingExtractionTemplateId, setEditingExtractionTemplateId] = useState<number | null>(null);
  const [extractionTestFile, setExtractionTestFile] = useState<File | null>(null);
  const [extractionTestTemplateId, setExtractionTestTemplateId] = useState<number | null>(null);
  const [extractionPreview, setExtractionPreview] = useState<DocumentExtractionPreviewResponse | null>(null);
  const [extractionTestFeedback, setExtractionTestFeedback] = useState<string | null>(null);
  const [showExtractionTrainer, setShowExtractionTrainer] = useState(false);
  const [stagedExtractionFields, setStagedExtractionFields] = useState<TrainerField[]>([]);
  const [suggestedExtractionFields, setSuggestedExtractionFields] = useState<DocumentExtractionSuggestedFieldDto[]>([]);
  const [trainerHighlights, setTrainerHighlights] = useState<PersistentHighlight[]>([]);
  const [pdfPageCount, setPdfPageCount] = useState(0);
  const [pdfScale, setPdfScale] = useState(1.55);
  const [pdfLoadError, setPdfLoadError] = useState<string | null>(null);
  const [selectionLoading, setSelectionLoading] = useState(false);
  const [mobileTrainerView, setMobileTrainerView] = useState<MobileTrainerView>('document');
  const [dragSelectionStart, setDragSelectionStart] = useState<DragPoint | null>(null);
  const [dragSelectionCurrent, setDragSelectionCurrent] = useState<DragPoint | null>(null);
  const pdfSelectionContainerRef = useRef<HTMLDivElement | null>(null);

  const [labelSets, setLabelSets] = useState<DocumentLabelSetDto[]>([]);
  const [summarisationTemplates, setSummarisationTemplates] = useState<DocumentSummarisationTemplateDto[]>([]);
  const [extractionTemplates, setExtractionTemplates] = useState<DocumentExtractionTemplateDto[]>([]);
  const [processingMailboxUser, setProcessingMailboxUser] = useState('');
  const [processingMaxEmails, setProcessingMaxEmails] = useState(20);
  const [emailProcessingResult, setEmailProcessingResult] = useState<Record<string, unknown> | string | null>(null);

  const getFriendlyError = useCallback((error: unknown): string => {
    const raw = error instanceof Error ? error.message : 'Unexpected error.';
    const normalized = raw.toLowerCase();
    if (normalized.includes('404') || normalized.includes('not found')) {
      return 'Document Hub API is not available on the current backend deployment. Deploy the backend feature branch to test save and preview actions.';
    }
    return raw;
  }, []);

  const setupHint = useMemo(() => {
    if (activeTab === 'classification') {
      return 'Upload one or more sample documents, then review auto-generated labels and prompts before saving the label set.';
    }

    if (activeTab === 'summarisation') {
      return 'Define a reusable summarisation goal and prompt. This can be selected when users upload future documents.';
    }

    return 'Upload a sample document, highlight values, and confirm field names. Corrections will improve future extraction runs.';
  }, [activeTab]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [loadedLabelSets, loadedSummaries, loadedExtractors] = await Promise.all([
        documentHubService.getLabelSets(),
        documentHubService.getSummarisationTemplates(),
        documentHubService.getExtractionTemplates(),
      ]);
      setLabelSets(loadedLabelSets);
      setSummarisationTemplates(loadedSummaries);
      setExtractionTemplates(loadedExtractors);
    } catch (error) {
      setFeedback(getFriendlyError(error));
    } finally {
      setLoading(false);
    }
  }, [getFriendlyError]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const syncMasterPromptToSuggestions = useCallback((prompt: string) => {
    setClassificationSuggestions((prev) =>
      prev.length === 0 ? prev : prev.map((item) => ({ ...item, suggestedPrompt: prompt }))
    );
  }, []);

  const syncMasterDescriptionToSuggestions = useCallback((description: string) => {
    setClassificationSuggestions((prev) =>
      prev.length === 0 ? prev : prev.map((item) => ({ ...item, suggestedDescription: description }))
    );
  }, []);

  const syncMasterLabelNameToSuggestions = useCallback((name: string) => {
    const derivedLabel = deriveClassificationLabelFromSetName(name);
    if (!derivedLabel) {
      return;
    }
    setClassificationSuggestions((prev) =>
      prev.length === 1 ? prev.map((item) => ({ ...item, suggestedLabel: derivedLabel })) : prev
    );
  }, []);

  const handleSaveLabelSet = async () => {
    const normalizedLabelSetName =
      labelSetName.trim() ||
      (classificationSuggestions[0]?.suggestedLabel
        ? `${classificationSuggestions[0].suggestedLabel.trim()} Label Set`
        : '');
    if (!normalizedLabelSetName) {
      setFeedback('Label set name is required.');
      return;
    }

    setLoading(true);
    setFeedback(null);
    try {
      const masterPrompt = classificationPrompt.trim();
      const masterDescription = labelSetDescription.trim();
      const labelsToSave =
        classificationSuggestions.length > 0
          ? classificationSuggestions
          : [
              {
                fileName: 'Manual seed',
                suggestedLabel:
                  normalizedLabelSetName
                    .trim()
                    .split(/\s+/)
                    .slice(0, 2)
                    .join(' ') || 'General Doc',
                suggestedDescription: '',
                suggestedPrompt: classificationPrompt.trim(),
                textPreview: '',
              },
            ];

      if (editingLabelSetId) {
        await documentHubService.updateLabelSet(editingLabelSetId, {
          labelSetName: normalizedLabelSetName.slice(0, 120),
          labelSetDescription: labelSetDescription.trim() || undefined,
          labels: labelsToSave.map((suggestion) => {
            const resolvedDescription = (
              classificationSuggestions.length === 1
                ? masterDescription || suggestion.suggestedDescription?.trim()
                : suggestion.suggestedDescription?.trim() || masterDescription || ''
            ).trim();
            const resolvedPrompt = (
              classificationSuggestions.length === 1
                ? masterPrompt || suggestion.suggestedPrompt?.trim()
                : suggestion.suggestedPrompt?.trim() || masterPrompt
            )?.trim() || 'Classify this document based on content and layout.';
            const resolvedLabel = (
              classificationSuggestions.length === 1
                ? deriveClassificationLabelFromSetName(normalizedLabelSetName) ||
                  suggestion.suggestedLabel?.trim() ||
                  'General Doc'
                : suggestion.suggestedLabel?.trim() || 'General Doc'
            ).slice(0, 80);

            return {
            classificationLabel: resolvedLabel || 'General Doc',
            classificationDescription: resolvedDescription ? resolvedDescription.slice(0, 2000) : undefined,
            classificationPrompt: resolvedPrompt.slice(0, 8000),
            seedDocumentName: suggestion.fileName,
            isAutoGenerated: true,
            isActive: true,
            };
          }),
        });
      } else {
        const created = await documentHubService.createLabelSet({
          labelSetName: normalizedLabelSetName.slice(0, 120),
          labelSetDescription: labelSetDescription.trim() || undefined,
        });

        for (const suggestion of labelsToSave) {
          const safeLabel = (suggestion.suggestedLabel || 'General Doc').trim().slice(0, 80);
          const safeDescription = suggestion.suggestedDescription?.trim().slice(0, 2000) || undefined;
          const safePrompt =
            (suggestion.suggestedPrompt || classificationPrompt || 'Classify this document based on content and layout.')
              .trim()
              .slice(0, 8000);

          await documentHubService.createClassificationLabel(created.documentLabelSetId, {
            classificationLabel: safeLabel || 'General Doc',
            classificationDescription: safeDescription,
            classificationPrompt: safePrompt,
            seedDocumentName: suggestion.fileName,
            isAutoGenerated: true,
          });
        }
      }

      setLabelSetName('');
      setLabelSetDescription('');
      setClassificationPrompt('Classify this document into the closest label based on both text and visual layout.');
      setClassificationFiles([]);
      setClassificationSuggestions([]);
      setEditingLabelSetId(null);
      setFeedback(
        editingLabelSetId
          ? `Classification label set updated with ${labelsToSave.length} label(s).`
          : `Classification label set saved with ${labelsToSave.length} label(s).`
      );
      await loadData();
    } catch (error) {
      setFeedback(getFriendlyError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSummarisationTemplate = async () => {
    if (!summarisationName.trim() || !summarisationPrompt.trim()) {
      setFeedback('Summarisation name and prompt are required.');
      return;
    }

    setLoading(true);
    setFeedback(null);
    setSummarisationFeedback(null);
    try {
      if (editingSummarisationTemplateId) {
        await documentHubService.updateSummarisationTemplate(editingSummarisationTemplateId, {
          summarisationName: summarisationName.trim(),
          summarisationDescription: summarisationDescription.trim() || undefined,
          summarisationPrompt: summarisationPrompt.trim(),
        });
      } else {
        await documentHubService.createSummarisationTemplate({
          summarisationName: summarisationName.trim(),
          summarisationDescription: summarisationDescription.trim() || undefined,
          summarisationPrompt: summarisationPrompt.trim(),
        });
      }

      setSummarisationName('');
      setSummarisationDescription('');
      setSummarisationTestFile(null);
      setEditingSummarisationTemplateId(null);
      setFeedback(editingSummarisationTemplateId ? 'Summarisation template updated.' : 'Summarisation template saved.');
      await loadData();
    } catch (error) {
      setFeedback(getFriendlyError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveExtractionTemplate = async () => {
    if (!extractionTemplateName.trim()) {
      setFeedback('Extraction template name is required.');
      return;
    }

    setLoading(true);
    setFeedback(null);
    try {
      if (editingExtractionTemplateId) {
        await documentHubService.updateExtractionTemplate(editingExtractionTemplateId, {
          extractionTemplateName: extractionTemplateName.trim(),
          extractionTemplateDescription: extractionTemplateDescription.trim() || undefined,
          fields: stagedExtractionFields.map((field) => ({
            fieldName: field.fieldName.trim(),
            exampleValue: field.exampleValue.trim() || undefined,
            boundingBoxJson: field.boundingBoxJson,
            pageNumber: field.pageNumber,
            isActive: true,
          })),
        });
      } else {
        const template = await documentHubService.createExtractionTemplate({
          extractionTemplateName: extractionTemplateName.trim(),
          extractionTemplateDescription: extractionTemplateDescription.trim() || undefined,
        });

        for (const field of stagedExtractionFields) {
          await documentHubService.createExtractionField(template.documentExtractionTemplateId, {
            fieldName: field.fieldName.trim(),
            exampleValue: field.exampleValue.trim() || undefined,
            boundingBoxJson: field.boundingBoxJson,
            pageNumber: field.pageNumber,
          });
        }
      }

      setExtractionTemplateName('');
      setExtractionTemplateDescription('');
      setExtractionTestFile(null);
      setExtractionPreview(null);
      setStagedExtractionFields([]);
      setSuggestedExtractionFields([]);
      setTrainerHighlights([]);
      setPdfPageCount(0);
      setPdfScale(1.55);
      setShowExtractionTrainer(false);
      setEditingExtractionTemplateId(null);
      setFeedback(editingExtractionTemplateId ? 'Extraction template updated.' : 'Extraction template saved.');
      await loadData();
    } catch (error) {
      setFeedback(getFriendlyError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleEditExtractionTemplate = (template: DocumentExtractionTemplateDto) => {
    setEditingExtractionTemplateId(template.documentExtractionTemplateId);
    setExtractionTestTemplateId(template.documentExtractionTemplateId);
    setExtractionTemplateName(template.extractionTemplateName);
    setExtractionTemplateDescription(template.extractionTemplateDescription ?? '');
    setStagedExtractionFields(
      (template.fields ?? []).map((field) => ({
        id: `field-${field.documentExtractionFieldId}`,
        fieldName: field.fieldName,
        exampleValue: field.exampleValue ?? '',
        boundingBoxJson: field.boundingBoxJson,
        pageNumber: field.pageNumber,
      }))
    );
    setShowExtractionTrainer(false);
    setFeedback(`Editing extraction template "${template.extractionTemplateName}".`);
  };

  const handleCancelEditExtraction = () => {
    setEditingExtractionTemplateId(null);
    setExtractionTemplateName('');
    setExtractionTemplateDescription('');
    setStagedExtractionFields([]);
    setSuggestedExtractionFields([]);
    setTrainerHighlights([]);
    setExtractionPreview(null);
    setExtractionTestFile(null);
    setShowExtractionTrainer(false);
    setFeedback('Extraction edit cancelled.');
  };

  const handleDeleteExtractionTemplate = async (templateId: number, templateName: string) => {
    const confirmed = window.confirm(`Delete extraction template "${templateName}"? This cannot be undone.`);
    if (!confirmed) {
      return;
    }

    setLoading(true);
    setFeedback(null);
    try {
      await documentHubService.deleteExtractionTemplate(templateId);
      if (editingExtractionTemplateId === templateId) {
        handleCancelEditExtraction();
      }
      if (extractionTestTemplateId === templateId) {
        setExtractionTestTemplateId(null);
      }
      setFeedback(`Deleted extraction template "${templateName}".`);
      await loadData();
    } catch (error) {
      setFeedback(getFriendlyError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicateExtractionTemplate = async (template: DocumentExtractionTemplateDto) => {
    setLoading(true);
    setFeedback(null);
    try {
      const duplicateName = duplicateDisplayName(template.extractionTemplateName, 200);
      const created = await documentHubService.createExtractionTemplate({
        extractionTemplateName: duplicateName,
        extractionTemplateDescription: template.extractionTemplateDescription?.trim() || undefined,
      });

      for (const field of template.fields ?? []) {
        await documentHubService.createExtractionField(created.documentExtractionTemplateId, {
          fieldName: field.fieldName,
          exampleValue: field.exampleValue?.trim() || undefined,
          anchorTextBefore: field.anchorTextBefore?.trim() || undefined,
          anchorTextAfter: field.anchorTextAfter?.trim() || undefined,
          fieldExtractionPrompt: field.fieldExtractionPrompt?.trim() || undefined,
          pageNumber: field.pageNumber,
          boundingBoxJson: field.boundingBoxJson,
        });
      }

      await loadData();
      setFeedback(`Duplicated extraction template as "${duplicateName}" (${template.fields?.length ?? 0} field(s)).`);
    } catch (error) {
      setFeedback(getFriendlyError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateClassificationSuggestions = async () => {
    if (classificationFiles.length === 0) {
      setFeedback('Upload one or more PDF files first.');
      return;
    }

    setLoading(true);
    setFeedback(null);
    try {
      const suggestions = await documentHubService.suggestClassificationLabels(classificationFiles);
      setClassificationSuggestions(suggestions);
      setFeedback(`Generated ${suggestions.length} label suggestions.`);
    } catch (error) {
      setFeedback(getFriendlyError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleRunClassificationTest = async () => {
    if (!classificationTestFile) {
      setClassificationTestFeedback('Upload a document file to test classification.');
      return;
    }

    setLoading(true);
    setFeedback(null);
    setClassificationTestFeedback(null);
    try {
      const result = await documentHubService.testClassification(classificationTestFile);
      setClassificationTestResult(result);
      setClassificationTestFeedback(`Classification test complete for ${result.fileName}.`);
    } catch (error) {
      setClassificationTestFeedback(getFriendlyError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleEditLabelSet = (labelSet: DocumentLabelSetDto) => {
    const setName = (labelSet.labelSetName ?? '').trim();
    const derivedLabelFromSetName = deriveClassificationLabelFromSetName(setName);
    const mappedSuggestions = (labelSet.labels ?? []).map((label) => ({
      fileName: label.seedDocumentName ?? 'Saved label',
      suggestedLabel: label.classificationLabel ?? '',
      suggestedDescription: label.classificationDescription ?? '',
      suggestedPrompt: label.classificationPrompt ?? '',
      textPreview: '',
    }));
    if (mappedSuggestions.length === 1 && derivedLabelFromSetName) {
      const storedLabel = mappedSuggestions[0].suggestedLabel.trim();
      if (!storedLabel || storedLabel.toLowerCase() !== derivedLabelFromSetName.toLowerCase()) {
        mappedSuggestions[0].suggestedLabel = derivedLabelFromSetName;
      }
    }
    const fallbackDescriptionFromLabels =
      mappedSuggestions.find((item) => item.suggestedDescription.trim().length > 0)?.suggestedDescription ?? '';

    setEditingLabelSetId(labelSet.documentLabelSetId);
    setLabelSetName(labelSet.labelSetName ?? '');
    setLabelSetDescription((labelSet.labelSetDescription ?? '').trim() || fallbackDescriptionFromLabels);
    setClassificationPrompt(
      labelSet.labels[0]?.classificationPrompt?.trim() ||
        'Classify this document into the closest label based on both text and visual layout.'
    );
    setClassificationSuggestions(mappedSuggestions);
    setFeedback(`Loaded "${labelSet.labelSetName}" into the form for editing.`);
  };

  const handleCancelEditLabelSet = () => {
    setEditingLabelSetId(null);
    setLabelSetName('');
    setLabelSetDescription('');
    setClassificationPrompt('Classify this document into the closest label based on both text and visual layout.');
    setClassificationFiles([]);
    setClassificationSuggestions([]);
    setFeedback('Classification edit cancelled.');
  };

  const handleDeleteLabelSet = async (labelSetId: number, labelSetName: string) => {
    const confirmed = window.confirm(`Delete classification label set "${labelSetName}"? This cannot be undone.`);
    if (!confirmed) {
      return;
    }

    setLoading(true);
    setFeedback(null);
    try {
      await documentHubService.deleteLabelSet(labelSetId);
      if (editingLabelSetId === labelSetId) {
        handleCancelEditLabelSet();
      }
      setFeedback(`Deleted classification label set "${labelSetName}".`);
      await loadData();
    } catch (error) {
      setFeedback(getFriendlyError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicateLabelSet = async (labelSet: DocumentLabelSetDto) => {
    setLoading(true);
    setFeedback(null);
    try {
      const duplicateName = duplicateDisplayName(labelSet.labelSetName, 120);
      const created = await documentHubService.createLabelSet({
        labelSetName: duplicateName,
        labelSetDescription: labelSet.labelSetDescription?.trim() || undefined,
      });

      for (const label of labelSet.labels ?? []) {
        await documentHubService.createClassificationLabel(created.documentLabelSetId, {
          classificationLabel: label.classificationLabel,
          classificationDescription: label.classificationDescription?.trim() || undefined,
          classificationPrompt: label.classificationPrompt,
          seedDocumentName: label.seedDocumentName?.trim() || undefined,
          isAutoGenerated: label.isAutoGenerated,
        });
      }

      await loadData();
      setFeedback(`Duplicated label set as "${duplicateName}" (${labelSet.labels?.length ?? 0} label(s)).`);
    } catch (error) {
      setFeedback(getFriendlyError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleRunSummarisationPreview = async () => {
    if (!summarisationTestFile) {
      setSummarisationFeedback('Upload a PDF to test summarisation.');
      return;
    }

    if (!summarisationPrompt.trim()) {
      setSummarisationFeedback('Summarisation prompt is required.');
      return;
    }

    setLoading(true);
    setFeedback(null);
    setSummarisationFeedback(null);
    try {
      const preview = await documentHubService.previewSummarisation(summarisationTestFile, summarisationPrompt.trim());
      setSummarisationPreview(preview);
      setSummarisationFeedback(`Preview generated for ${preview.fileName}.`);
    } catch (error) {
      setSummarisationFeedback(getFriendlyError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleEditSummarisationTemplate = (template: DocumentSummarisationTemplateDto) => {
    setEditingSummarisationTemplateId(template.documentSummarisationTemplateId);
    setSummarisationName(template.summarisationName);
    setSummarisationDescription(template.summarisationDescription ?? '');
    setSummarisationPrompt(template.summarisationPrompt);
    setSummarisationFeedback(`Editing template "${template.summarisationName}".`);
  };

  const handleCancelEditSummarisation = () => {
    setEditingSummarisationTemplateId(null);
    setSummarisationName('');
    setSummarisationDescription('');
    setSummarisationPrompt('Summarise this document using headings, key decisions, and next actions.');
    setSummarisationFeedback('Edit cancelled.');
  };

  const handleDeleteSummarisationTemplate = async (templateId: number, templateName: string) => {
    const confirmed = window.confirm(`Delete summarisation template "${templateName}"? This cannot be undone.`);
    if (!confirmed) {
      return;
    }

    setLoading(true);
    setFeedback(null);
    setSummarisationFeedback(null);
    try {
      await documentHubService.deleteSummarisationTemplate(templateId);
      if (editingSummarisationTemplateId === templateId) {
        handleCancelEditSummarisation();
      }
      setFeedback(`Deleted summarisation template "${templateName}".`);
      await loadData();
    } catch (error) {
      setFeedback(getFriendlyError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicateSummarisationTemplate = async (template: DocumentSummarisationTemplateDto) => {
    setLoading(true);
    setFeedback(null);
    setSummarisationFeedback(null);
    try {
      const duplicateName = duplicateDisplayName(template.summarisationName, 200);
      await documentHubService.createSummarisationTemplate({
        summarisationName: duplicateName,
        summarisationDescription: template.summarisationDescription?.trim() || undefined,
        summarisationPrompt: template.summarisationPrompt,
      });
      await loadData();
      setFeedback(`Duplicated summarisation template as "${duplicateName}".`);
    } catch (error) {
      setFeedback(getFriendlyError(error));
    } finally {
      setLoading(false);
    }
  };

  const handlePrepareExtractionPreview = async () => {
    if (!extractionTestFile) {
      setFeedback('Upload a PDF or Excel (.xlsx) file to start extraction training.');
      return;
    }

    if (!isPdfTrainingFile(extractionTestFile) && !isSpreadsheetTrainingFile(extractionTestFile)) {
      setFeedback('Extraction training supports PDF and .xlsx files.');
      return;
    }

    setLoading(true);
    setFeedback(null);
    try {
      const preview = await documentHubService.previewExtraction(extractionTestFile);
      setExtractionPreview(preview);
      setSuggestedExtractionFields(preview.suggestedFields ?? []);
      setTrainerHighlights([]);
      setPdfPageCount(0);
      setPdfScale(1.55);
      setPdfLoadError(null);
      setMobileTrainerView('document');
      setShowExtractionTrainer(true);
      const trainerLabel = isSpreadsheetTrainingFile(extractionTestFile) ? 'spreadsheet text' : 'PDF';
      setFeedback(`Extraction preview ready. Select text in the ${trainerLabel} preview to build fields.`);
    } catch (error) {
      setFeedback(getFriendlyError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleRunExtractionTest = async () => {
    if (!extractionTestFile) {
      setExtractionTestFeedback('Upload a PDF or Excel (.xlsx) file to test entity extraction.');
      return;
    }

    setLoading(true);
    setFeedback(null);
    setExtractionTestFeedback(null);
    try {
      const preview = await documentHubService.previewExtraction(extractionTestFile, extractionTestTemplateId);
      setExtractionPreview(preview);
      setSuggestedExtractionFields(preview.suggestedFields ?? []);
      const suffix =
        preview.previewMode === 'template_fields'
          ? ' Matches workflow extraction against your chosen template.'
          : ' Generic AI guesses only—pick a saved template above to preview real field extraction.';
      setExtractionTestFeedback(`Entity extraction test complete for ${preview.fileName}.${suffix}`);
    } catch (error) {
      setExtractionTestFeedback(getFriendlyError(error));
    } finally {
      setLoading(false);
    }
  };

  const appendTrainerFields = (
    fields: DocumentExtractionSuggestedFieldDto[],
    metadata?: Pick<TrainerField, 'boundingBoxJson' | 'pageNumber'>
  ) => {
    setStagedExtractionFields((prev) => {
      const merged = [...prev];
      for (const field of fields) {
        const fieldName = field.fieldName.trim();
        const exampleValue = field.exampleValue.trim();
        if (!fieldName || !exampleValue) {
          continue;
        }

        const alreadyExists = merged.some(
          (item) =>
            item.fieldName.trim().toLowerCase() === fieldName.toLowerCase() &&
            item.exampleValue.trim().toLowerCase() === exampleValue.toLowerCase()
        );
        if (alreadyExists) {
          continue;
        }

        merged.push({
          id: `field-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          fieldName,
          exampleValue,
          boundingBoxJson: metadata?.boundingBoxJson,
          pageNumber: metadata?.pageNumber,
        });
      }

      return merged;
    });
  };

  const runSelectionCapture = async (
    selected: string,
    rects: Array<{ left: number; top: number; width: number; height: number }>,
    options?: { spreadsheetCells?: SpreadsheetCellCapture[] }
  ) => {
    if (!selected) {
      setFeedback('Select text in the document preview first.');
      return;
    }

    setTrainerHighlights((prev) => [
      ...prev,
      {
        id: `hl-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        color: HIGHLIGHT_COLORS[prev.length % HIGHLIGHT_COLORS.length],
        selectedText: selected,
        rects,
      },
    ]);

    const spreadsheetCells = options?.spreadsheetCells ?? [];
    const contextFields =
      spreadsheetCells.length > 0 && hasSpreadsheetCellContext(spreadsheetCells)
        ? spreadsheetCells
            .filter((cell) => cell.value.trim())
            .map((cell) => ({
              fieldName: buildSpreadsheetFieldName(cell),
              exampleValue: cell.value.trim(),
            }))
        : [];

    setSelectionLoading(true);
    try {
      const metadata = { boundingBoxJson: JSON.stringify(rects) };
      if (contextFields.length > 0) {
        appendTrainerFields(contextFields, metadata);
        const summary = contextFields.map((field) => `${field.fieldName} = ${field.exampleValue}`).join(', ');
        setFeedback(`Captured ${contextFields.length} cell(s): ${summary}`);
        return;
      }

      const aiFields = await documentHubService.suggestExtractionFromSelection({
        selectedText: selected,
        extractedText: extractionPreview?.extractedText ?? '',
      });
      if (aiFields.length > 0) {
        appendTrainerFields(aiFields, metadata);
      } else {
        appendTrainerFields([{ fieldName: 'extracted_field', exampleValue: selected }], metadata);
      }
    } catch (error) {
      setFeedback(getFriendlyError(error));
      appendTrainerFields([{ fieldName: 'extracted_field', exampleValue: selected }], {
        boundingBoxJson: JSON.stringify(rects),
      });
    } finally {
      setSelectionLoading(false);
    }
  };

  const captureSelectedText = async () => {
    const selection = window.getSelection();
    const selected = selection?.toString().trim() || '';
    if (!selected) {
      setFeedback('Select text in the document preview area.');
      return;
    }

    const range = selection?.rangeCount ? selection.getRangeAt(0) : null;
    const container = pdfSelectionContainerRef.current;
    if (!range || !container || !container.contains(range.commonAncestorContainer)) {
      setFeedback('Selection must be inside the document preview area.');
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const rects = Array.from(range.getClientRects())
      .filter((rect) => rect.width > 1 && rect.height > 1)
      .map((rect) => ({
        left: rect.left - containerRect.left + container.scrollLeft,
        top: rect.top - containerRect.top + container.scrollTop,
        width: rect.width,
        height: rect.height,
      }));

    selection?.removeAllRanges();
    if (rects.length === 0) {
      setFeedback('Could not read the selected area. Try dragging a larger region.');
      return;
    }

    const spreadsheetCells =
      container && isSpreadsheetTrainingFile(extractionTestFile)
        ? collectSpreadsheetCellsFromContainer(container, rects)
        : [];

    await runSelectionCapture(selected, rects, { spreadsheetCells });
  };

  const getPointInSelectionContainer = (event: React.MouseEvent<HTMLDivElement>): DragPoint | null => {
    const container = pdfSelectionContainerRef.current;
    if (!container) {
      return null;
    }

    const bounds = container.getBoundingClientRect();
    return {
      x: event.clientX - bounds.left + container.scrollLeft,
      y: event.clientY - bounds.top + container.scrollTop,
    };
  };

  const getNormalizedRect = (start: DragPoint, end: DragPoint) => {
    const left = Math.min(start.x, end.x);
    const top = Math.min(start.y, end.y);
    const width = Math.abs(start.x - end.x);
    const height = Math.abs(start.y - end.y);
    return { left, top, width, height };
  };

  const collectTextFromRect = (targetRect: { left: number; top: number; width: number; height: number }) => {
    const container = pdfSelectionContainerRef.current;
    if (!container) {
      return '';
    }

    const containerBounds = container.getBoundingClientRect();
    const spans = Array.from(
      container.querySelectorAll('.react-pdf__Page__textContent span, [data-trainer-selectable]')
    );
    const collected: string[] = [];

    for (const span of spans) {
      const text = (span.textContent || '').trim();
      if (!text) {
        continue;
      }

      const spanRect = span.getBoundingClientRect();
      const relativeRect = {
        left: spanRect.left - containerBounds.left + container.scrollLeft,
        top: spanRect.top - containerBounds.top + container.scrollTop,
        width: spanRect.width,
        height: spanRect.height,
      };

      const intersects =
        relativeRect.left < targetRect.left + targetRect.width &&
        relativeRect.left + relativeRect.width > targetRect.left &&
        relativeRect.top < targetRect.top + targetRect.height &&
        relativeRect.top + relativeRect.height > targetRect.top;

      if (intersects) {
        collected.push(text);
      }
    }

    return collected.join(' ').replace(/\s+/g, ' ').trim();
  };

  const handleDragSelectionStart = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.button !== 0 || selectionLoading) {
      return;
    }

    const point = getPointInSelectionContainer(event);
    if (!point) {
      return;
    }

    setDragSelectionStart(point);
    setDragSelectionCurrent(point);
  };

  const handleDragSelectionMove = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!dragSelectionStart) {
      return;
    }

    const point = getPointInSelectionContainer(event);
    if (!point) {
      return;
    }

    setDragSelectionCurrent(point);
  };

  const handleDragSelectionEnd = async (event: React.MouseEvent<HTMLDivElement>) => {
    if (!dragSelectionStart) {
      return;
    }

    const point = getPointInSelectionContainer(event);
    const endPoint = point ?? dragSelectionCurrent ?? dragSelectionStart;
    const normalized = getNormalizedRect(dragSelectionStart, endPoint);

    setDragSelectionStart(null);
    setDragSelectionCurrent(null);

    if (normalized.width < 8 || normalized.height < 8) {
      return;
    }

    const container = pdfSelectionContainerRef.current;
    const spreadsheetCells =
      container && isSpreadsheetTrainingFile(extractionTestFile)
        ? collectSpreadsheetCellsFromContainer(container, [normalized])
        : [];

    const selected =
      spreadsheetCells.length > 0
        ? spreadsheetCells.map((cell) => cell.value).join(' ')
        : collectTextFromRect(normalized);
    if (!selected) {
      setFeedback('No selectable text found in that area. Try a larger drag box.');
      return;
    }

    await runSelectionCapture(selected, [normalized], { spreadsheetCells });
  };

  const dragPreviewRect =
    dragSelectionStart && dragSelectionCurrent ? getNormalizedRect(dragSelectionStart, dragSelectionCurrent) : null;

  const updateStagedField = (id: string, patch: Partial<Pick<TrainerField, 'fieldName' | 'exampleValue'>>) => {
    setStagedExtractionFields((prev) => prev.map((field) => (field.id === id ? { ...field, ...patch } : field)));
  };

  const removeStagedField = (id: string) => {
    setStagedExtractionFields((prev) => prev.filter((field) => field.id !== id));
  };

  const addSuggestedField = (field: DocumentExtractionSuggestedFieldDto) => {
    appendTrainerFields([field]);
  };

  const handleTriggerEmailProcessing = async () => {
    setLoading(true);
    setFeedback(null);
    try {
      const response = await documentHubService.triggerPropertyHubEmailProcessing({
        mailboxUser: processingMailboxUser.trim() || undefined,
        maxEmails: Number.isFinite(processingMaxEmails) ? processingMaxEmails : undefined,
      });
      const result = response.processingResult;
      setEmailProcessingResult(
        typeof result === 'string' || (typeof result === 'object' && result !== null)
          ? (result as Record<string, unknown> | string)
          : null
      );
      setFeedback(response.message || 'Email processing completed.');
    } catch (error) {
      setFeedback(getFriendlyError(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <h2 className="text-2xl font-semibold tracking-tight text-slate-900">Document Hub</h2>
        <p className="mt-1 text-sm text-slate-500">
          Configure global document classification, text summarisation, and entity extraction templates.
        </p>
        <div className="mt-3 rounded-lg border border-indigo-100 bg-indigo-50 px-3 py-2 text-xs text-indigo-700">{setupHint}</div>
        {feedback && (
          <p className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">{feedback}</p>
        )}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">Email Trigger (Inbox/Property Hub)</p>
        <div className="mt-2 grid gap-2 md:grid-cols-[1fr_130px_auto]">
          <input
            value={processingMailboxUser}
            onChange={(event) => setProcessingMailboxUser(event.target.value)}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
            placeholder="Mailbox user override (optional)"
          />
          <input
            type="number"
            min={1}
            max={100}
            value={processingMaxEmails}
            onChange={(event) => setProcessingMaxEmails(Number(event.target.value || 20))}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
            placeholder="Max emails"
          />
          <button
            type="button"
            onClick={handleTriggerEmailProcessing}
            disabled={loading}
            className="inline-flex items-center justify-center rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-60"
          >
            {loading ? 'Processing...' : 'Process Property Hub Emails'}
          </button>
        </div>
        {emailProcessingResult !== null && (
          <>
            <p className="mt-2 text-[11px] text-slate-500">
              Each message may include{' '}
              <code className="rounded bg-slate-100 px-1">workflowContextPreview</code> (summarisation, template-driven
              field tokens from <code className="rounded bg-slate-100 px-1">RunExtraction</code>,{' '}
              <code className="rounded bg-slate-100 px-1">workflowMeta_extractionTemplateId</code>,{' '}
              <code className="rounded bg-slate-100 px-1">workflowMeta_extractionCorpus</code> ({' '}
              <code className="rounded bg-slate-100 px-1">attachments_bundle</code> vs{' '}
              <code className="rounded bg-slate-100 px-1">full_email_fallback</code>), and truncated{' '}
              <code className="rounded bg-slate-100 px-1">extractionJson</code>).               When OpenAI credentials are configured on the email processor function, workflows use GPT for extraction
              (template fields + examples) with regex fills for gaps (<code className="rounded bg-slate-100 px-1">workflowMeta_openAiFilledFields</code>
              ).
            </p>
            <pre className="mt-2 max-h-[36rem] overflow-auto rounded border border-slate-200 bg-slate-50 p-2 text-[11px] text-slate-700">
              {JSON.stringify(emailProcessingResult, null, 2)}
            </pre>
          </>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        <button type="button" className={tabClass(activeTab === 'classification')} onClick={() => setActiveTab('classification')}>
          Classification
        </button>
        <button type="button" className={tabClass(activeTab === 'summarisation')} onClick={() => setActiveTab('summarisation')}>
          Text Summarisation
        </button>
        <button type="button" className={tabClass(activeTab === 'extraction')} onClick={() => setActiveTab('extraction')}>
          Entity Extraction
        </button>
      </div>

      {activeTab === 'classification' && (
        <div className="grid gap-6 xl:grid-cols-[1.2fr_1fr]">
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <h3 className="text-lg font-semibold text-slate-900">Classification Label Set</h3>
            <div className="mt-4 space-y-4">
              <label className="block">
                <span className="text-sm font-medium text-slate-700">Label Set Name</span>
                <p className="mt-0.5 text-xs text-slate-500">
                  For single-label sets, this also drives the classification label shown below (what workflows match on).
                </p>
                <input
                  value={labelSetName}
                  onChange={(event) => {
                    const next = event.target.value;
                    setLabelSetName(next);
                    syncMasterLabelNameToSuggestions(next);
                  }}
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
                  placeholder="e.g. Tenancy Documents"
                />
              </label>
              <label className="block">
                <span className="text-sm font-medium text-slate-700">Label Set Description</span>
                <p className="mt-0.5 text-xs text-slate-500">
                  Set-level notes. For single-label sets, editing the label set name or this field also updates the
                  classification label and description below.
                </p>
                <textarea
                  value={labelSetDescription}
                  onChange={(event) => {
                    const next = event.target.value;
                    setLabelSetDescription(next);
                    syncMasterDescriptionToSuggestions(next);
                  }}
                  className="mt-1 min-h-24 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
                  placeholder="Describe what document family this label set should classify."
                />
              </label>
              <label className="block">
                <span className="text-sm font-medium text-slate-700">Classification Prompt Template</span>
                <p className="mt-0.5 text-xs text-slate-500">
                  Prompt sent to the classifier for every label in this set. Editing here updates the label prompt below
                  immediately for single-label sets.
                </p>
                <textarea
                  value={classificationPrompt}
                  onChange={(event) => {
                    const next = event.target.value;
                    setClassificationPrompt(next);
                    syncMasterPromptToSuggestions(next);
                  }}
                  className="mt-1 min-h-28 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
                />
              </label>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <h3 className="text-lg font-semibold text-slate-900">Seed Documents</h3>
            <p className="mt-1 text-sm text-slate-500">
              Upload one or more sample PDFs to auto-generate 2-word labels and editable prompts.
            </p>
            <div className="mt-4 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
              <input
                type="file"
                multiple
                accept=".pdf"
                onChange={(event) => setClassificationFiles(Array.from(event.target.files ?? []))}
                className="mx-auto block text-sm text-slate-700"
              />
              <p className="mt-2 text-xs">
                {classificationFiles.length === 0
                  ? 'No files selected.'
                  : `${classificationFiles.length} file(s) selected.`}
              </p>
            </div>
            <button
              type="button"
              onClick={handleGenerateClassificationSuggestions}
              disabled={loading}
              className="mt-4 mr-2 inline-flex items-center rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:border-slate-400"
            >
              {loading ? 'Generating...' : 'Generate Labels from Uploads'}
            </button>
            <button
              type="button"
              onClick={handleSaveLabelSet}
              disabled={loading}
              className="mt-4 inline-flex items-center rounded-lg bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-700"
            >
              {loading ? 'Saving...' : editingLabelSetId ? 'Update Label Set' : 'Save Label Set'}
            </button>
            {editingLabelSetId && (
              <button
                type="button"
                onClick={handleCancelEditLabelSet}
                disabled={loading}
                className="mt-4 ml-2 inline-flex items-center rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:border-slate-400"
              >
                Cancel Edit
              </button>
            )}
            <div className="mt-4 rounded-lg border border-indigo-200 bg-indigo-50/60 p-3 text-xs text-slate-700 shadow-sm">
              <p className="flex items-center gap-2 font-semibold text-indigo-900">
                Saved Label Sets
                <span className="inline-flex items-center rounded-full border border-indigo-200 bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-700">
                  Saved
                </span>
              </p>
              {labelSets.length === 0 ? (
                <p className="mt-1">No label sets saved yet.</p>
              ) : (
                <ul className="mt-2 space-y-2">
                  {labelSets.slice(0, 5).map((item) => (
                    <li
                      key={item.documentLabelSetId}
                      className="flex items-center justify-between rounded-md border border-indigo-100 bg-white px-2.5 py-1.5 shadow-sm transition hover:border-indigo-200 hover:shadow"
                    >
                      <span>
                        {item.labelSetName} ({item.labels.length} labels)
                      </span>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => handleEditLabelSet(item)}
                          disabled={loading}
                          className="rounded border border-slate-300 px-2 py-0.5 text-[11px] font-medium text-slate-700 hover:border-slate-400 disabled:opacity-60"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDuplicateLabelSet(item)}
                          disabled={loading}
                          className="rounded border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-700 hover:border-indigo-300 disabled:opacity-60"
                        >
                          Duplicate
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteLabelSet(item.documentLabelSetId, item.labelSetName)}
                          disabled={loading}
                          className="rounded border border-rose-200 bg-rose-50 px-2 py-0.5 text-[11px] font-medium text-rose-700 hover:border-rose-300 disabled:opacity-60"
                        >
                          Delete
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {classificationSuggestions.length > 0 && (
              <div className="mt-4 space-y-2 rounded-lg border border-slate-200 bg-white p-3 text-xs">
                <p className="font-semibold text-slate-700">Label details (saved with this set)</p>
                <p className="text-[11px] text-slate-500">
                  These values are what classification and email workflows use. The fields above stay in sync while you
                  edit.
                </p>
                {classificationSuggestions.map((item, idx) => (
                  <div key={`${item.fileName}-${idx}`} className="rounded border border-slate-200 p-2">
                    <p className="font-medium text-slate-800">{item.fileName}</p>
                    <p className="mt-1 text-[10px] font-medium uppercase tracking-wide text-slate-500">Classification label</p>
                    <input
                      value={item.suggestedLabel}
                      onChange={(event) =>
                        setClassificationSuggestions((prev) =>
                          prev.map((x, i) => (i === idx ? { ...x, suggestedLabel: event.target.value } : x))
                        )
                      }
                      className="mt-1 w-full rounded border border-slate-300 px-2 py-1"
                    />
                    <p className="mt-2 text-[10px] font-medium uppercase tracking-wide text-slate-500">Label description</p>
                    <textarea
                      value={item.suggestedDescription}
                      onChange={(event) => {
                        const next = event.target.value;
                        setClassificationSuggestions((prev) =>
                          prev.map((x, i) => (i === idx ? { ...x, suggestedDescription: next } : x))
                        );
                        if (classificationSuggestions.length === 1) {
                          setLabelSetDescription(next);
                        }
                      }}
                      className="mt-1 min-h-16 w-full rounded border border-slate-300 px-2 py-1"
                    />
                    <p className="mt-2 text-[10px] font-medium uppercase tracking-wide text-slate-500">Classification prompt</p>
                    <textarea
                      value={item.suggestedPrompt}
                      onChange={(event) => {
                        const next = event.target.value;
                        setClassificationSuggestions((prev) =>
                          prev.map((x, i) => (i === idx ? { ...x, suggestedPrompt: next } : x))
                        );
                        if (classificationSuggestions.length === 1) {
                          setClassificationPrompt(next);
                        }
                      }}
                      className="mt-1 min-h-16 w-full rounded border border-slate-300 px-2 py-1"
                    />
                  </div>
                ))}
              </div>
            )}
            <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
              <p className="font-semibold text-slate-800">Test Classification</p>
              <p className="mt-1 text-slate-600">
                Upload any supported document and run a classification test against active labels to preview match score and
                explainability.
              </p>
              <label className="mt-3 block">
                <span className="text-[11px] font-medium text-slate-600">Test Document Upload</span>
                <input
                  type="file"
                  accept=".pdf,.xlsx,.txt,.csv,.json,.xml,.log,.md"
                  onChange={(event) => setClassificationTestFile(event.target.files?.[0] ?? null)}
                  className="mt-1 w-full rounded border border-slate-300 bg-white px-2 py-1 text-xs"
                />
              </label>
              <button
                type="button"
                onClick={handleRunClassificationTest}
                disabled={loading}
                className="mt-3 inline-flex items-center rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:border-slate-400"
              >
                {loading ? 'Testing...' : 'Test Classification'}
              </button>
              {classificationTestFeedback && (
                <div className="mt-3 rounded border border-slate-200 bg-white p-2 text-[11px] text-slate-700">
                  {classificationTestFeedback}
                </div>
              )}
              {classificationTestResult && (
                <div className="mt-3 rounded border border-indigo-100 bg-indigo-50 p-3 text-[11px] text-indigo-900">
                  <p>
                    <span className="font-semibold">Result:</span> {classificationTestResult.classificationLabel}
                  </p>
                  <p className="mt-1">
                    <span className="font-semibold">Description:</span>{' '}
                    {classificationTestResult.classificationDescription?.trim() || 'No matching label description available.'}
                  </p>
                  <p className="mt-1">
                    <span className="font-semibold">Score:</span> {classificationTestResult.classificationScore}
                  </p>
                  <p className="mt-1">
                    <span className="font-semibold">Explainability:</span> {classificationTestResult.classificationExplainability}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'summarisation' && (
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <h3 className="text-lg font-semibold text-slate-900">Text Summarisation Template</h3>
          <div className="mt-4 grid gap-4">
            <label className="block">
              <span className="text-sm font-medium text-slate-700">Text Summarisation Name</span>
              <input
                value={summarisationName}
                onChange={(event) => setSummarisationName(event.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
                placeholder="e.g. Compliance Brief"
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-slate-700">Text Summarisation Description</span>
              <textarea
                value={summarisationDescription}
                onChange={(event) => setSummarisationDescription(event.target.value)}
                className="mt-1 min-h-24 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
                placeholder="Describe the summary goal and expected tone."
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-slate-700">Text Summarisation Prompt</span>
              <textarea
                value={summarisationPrompt}
                onChange={(event) => setSummarisationPrompt(event.target.value)}
                className="mt-1 min-h-32 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-slate-700">Test Document Upload (temporary)</span>
              <input
                type="file"
                accept=".pdf"
                onChange={(event) => setSummarisationTestFile(event.target.files?.[0] ?? null)}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
            </label>
          </div>
          <button
            type="button"
            onClick={handleRunSummarisationPreview}
            disabled={loading}
            className="mt-4 mr-2 inline-flex items-center rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:border-slate-400"
          >
            {loading ? 'Testing...' : 'Test Prompt Against Document'}
          </button>
          <button
            type="button"
            onClick={handleSaveSummarisationTemplate}
            disabled={loading}
            className="mt-4 inline-flex items-center rounded-lg bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-700"
          >
            {loading ? 'Saving...' : editingSummarisationTemplateId ? 'Update Summarisation Template' : 'Save Summarisation Template'}
          </button>
          {editingSummarisationTemplateId && (
            <button
              type="button"
              onClick={handleCancelEditSummarisation}
              disabled={loading}
              className="mt-4 ml-2 inline-flex items-center rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:border-slate-400"
            >
              Cancel Edit
            </button>
          )}
          {summarisationFeedback && (
            <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">{summarisationFeedback}</div>
          )}
          <div className="mt-4 rounded-lg border border-indigo-200 bg-indigo-50/60 p-3 text-xs text-slate-700 shadow-sm">
            <p className="flex items-center gap-2 font-semibold text-indigo-900">
              Saved Summarisation Templates
              <span className="inline-flex items-center rounded-full border border-indigo-200 bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-700">
                Saved
              </span>
            </p>
            {summarisationTemplates.length === 0 ? (
              <p className="mt-1">No templates saved yet.</p>
            ) : (
              <ul className="mt-2 space-y-2">
                {summarisationTemplates.slice(0, 5).map((item) => (
                  <li key={item.documentSummarisationTemplateId} className="flex items-center justify-between rounded-md border border-indigo-100 bg-white px-2.5 py-1.5 shadow-sm transition hover:border-indigo-200 hover:shadow">
                    <span>{item.summarisationName}</span>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => handleEditSummarisationTemplate(item)}
                        disabled={loading}
                        className="rounded border border-slate-300 px-2 py-0.5 text-[11px] font-medium text-slate-700 hover:border-slate-400 disabled:opacity-60"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDuplicateSummarisationTemplate(item)}
                        disabled={loading}
                        className="rounded border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-700 hover:border-indigo-300 disabled:opacity-60"
                      >
                        Duplicate
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteSummarisationTemplate(item.documentSummarisationTemplateId, item.summarisationName)}
                        disabled={loading}
                        className="rounded border border-rose-200 bg-rose-50 px-2 py-0.5 text-[11px] font-medium text-rose-700 hover:border-rose-300 disabled:opacity-60"
                      >
                        Delete
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {summarisationPreview && (
            <div className="mt-4 rounded-lg border border-indigo-100 bg-indigo-50 p-3 text-xs text-indigo-900">
              <p className="font-semibold">Summary Preview ({summarisationPreview.fileName})</p>
              <p className="mt-2 whitespace-pre-wrap">{summarisationPreview.summary}</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'extraction' && (
        <div className="grid gap-6 xl:grid-cols-[1.3fr_1fr]">
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <h3 className="text-lg font-semibold text-slate-900">Entity Extraction Template</h3>
            <div className="mt-4 space-y-4">
              <label className="block">
                <span className="text-sm font-medium text-slate-700">Template Name</span>
                <input
                  value={extractionTemplateName}
                  onChange={(event) => setExtractionTemplateName(event.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
                  placeholder="e.g. Account Statement Fields"
                />
              </label>
              <label className="block">
                <span className="text-sm font-medium text-slate-700">Template Description</span>
                <textarea
                  value={extractionTemplateDescription}
                  onChange={(event) => setExtractionTemplateDescription(event.target.value)}
                  className="mt-1 min-h-24 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
                  placeholder="Describe when this extraction should be used."
                />
              </label>
              <label className="block">
                <span className="text-sm font-medium text-slate-700">Upload Training Document (temporary)</span>
                <input
                  type="file"
                  accept=".pdf,.xlsx"
                  onChange={(event) => setExtractionTestFile(event.target.files?.[0] ?? null)}
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </label>
              <label className="block">
                <span className="text-sm font-medium text-slate-700">
                  Template for &quot;Test Entity Extraction&quot;
                </span>
                <select
                  value={extractionTestTemplateId ?? ''}
                  onChange={(event) =>
                    setExtractionTestTemplateId(event.target.value ? Number(event.target.value) : null)
                  }
                  className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
                >
                  <option value="">Generic AI suggestions (does not match saved extraction fields)</option>
                  {extractionTemplates.map((item) => (
                    <option key={item.documentExtractionTemplateId} value={item.documentExtractionTemplateId}>
                      {item.extractionTemplateName}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-slate-500">
                  Choose one of your saved templates to preview the same line-based extraction that workflows and email
                  processing use. Leave empty only if you want rough AI guesses.
                </p>
              </label>
              <button
                type="button"
                onClick={handlePrepareExtractionPreview}
                disabled={loading}
                className="inline-flex items-center rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:border-slate-400"
              >
                {loading ? 'Preparing...' : 'Open Extraction Trainer'}
              </button>
              <button
                type="button"
                onClick={handleRunExtractionTest}
                disabled={loading}
                className="ml-2 inline-flex items-center rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:border-slate-400"
              >
                {loading ? 'Testing...' : 'Test Entity Extraction'}
              </button>
              {extractionTestFeedback && (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
                  {extractionTestFeedback}
                </div>
              )}
              {extractionPreview && (
                <div className="rounded-lg border border-indigo-100 bg-indigo-50 p-3 text-xs text-indigo-900">
                  <p className="font-semibold">Extraction Test Preview ({extractionPreview.fileName})</p>
                  <p className="mt-1 text-slate-700">
                    {extractionPreview.previewMode === 'template_fields'
                      ? `Template-backed extraction (${(extractionPreview.suggestedFields ?? []).length} fields from template #${
                          extractionPreview.extractionTemplateId ?? '?'
                        })`
                      : `AI-suggested fields: ${(extractionPreview.suggestedFields ?? []).length}`}
                  </p>
                  {extractionPreview.previewMode === 'template_fields' &&
                    extractionPreview.extractionTemplateId != null &&
                    extractionTestTemplateId !== extractionPreview.extractionTemplateId && (
                      <p className="mt-1 text-amber-800">
                        These results are for template #{extractionPreview.extractionTemplateId}. Your dropdown selection
                        does not match—pick that template or run the test again.
                      </p>
                    )}
                  <div className="mt-2 max-h-40 overflow-auto rounded border border-indigo-200 bg-white p-2">
                    {(extractionPreview.suggestedFields ?? []).length === 0 ? (
                      <p className="text-slate-500">No fields suggested.</p>
                    ) : (
                      <ul className="space-y-1">
                        {(extractionPreview.suggestedFields ?? []).map((field, idx) => (
                          <li key={`${field.fieldName}-${idx}`}>
                            <span className="font-semibold">{field.fieldName}:</span>{' '}
                            {field.exampleValue || (
                              <span className="text-amber-700">(empty—no matching line in extracted text)</span>
                            )}
                            {field.normalizedToken ? (
                              <span className="ml-1 text-slate-500">
                                <code className="rounded bg-slate-100 px-1">{`{field:${field.normalizedToken}}`}</code>
                              </span>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <h3 className="text-lg font-semibold text-slate-900">Captured Fields</h3>
            <div className="mt-4 space-y-4">
              {stagedExtractionFields.length === 0 ? (
                <p className="text-sm text-slate-500">No fields captured yet. Use Open Extraction Trainer.</p>
              ) : (
                stagedExtractionFields.map((field) => (
                  <div key={field.id} className="rounded-lg border border-slate-200 p-2 text-sm">
                    <p className="font-medium text-slate-800">{field.fieldName}</p>
                    <p className="text-slate-600">{field.exampleValue}</p>
                  </div>
                ))
              )}
              <p className="rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
                Future correction feedback will be saved and reused to improve extraction quality over time.
              </p>
            </div>
            <button
              type="button"
              onClick={handleSaveExtractionTemplate}
              disabled={loading}
              className="mt-4 inline-flex items-center rounded-lg bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-700"
            >
              {loading ? 'Saving...' : editingExtractionTemplateId ? 'Update Extraction Template' : 'Complete Extraction Template'}
            </button>
            {editingExtractionTemplateId && (
              <button
                type="button"
                onClick={handleCancelEditExtraction}
                disabled={loading}
                className="mt-4 ml-2 inline-flex items-center rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:border-slate-400"
              >
                Cancel Edit
              </button>
            )}
            <div className="mt-4 rounded-lg border border-indigo-200 bg-indigo-50/60 p-3 text-xs text-slate-700 shadow-sm">
              <p className="flex items-center gap-2 font-semibold text-indigo-900">
                Saved Extraction Templates
                <span className="inline-flex items-center rounded-full border border-indigo-200 bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-700">
                  Saved
                </span>
              </p>
              {extractionTemplates.length === 0 ? (
                <p className="mt-1">No templates saved yet.</p>
              ) : (
                <ul className="mt-2 space-y-2">
                  {extractionTemplates.slice(0, 5).map((item) => (
                    <li
                      key={item.documentExtractionTemplateId}
                      className="flex items-center justify-between rounded-md border border-indigo-100 bg-white px-2.5 py-1.5 shadow-sm transition hover:border-indigo-200 hover:shadow"
                    >
                      <span>
                        {item.extractionTemplateName} ({item.fields.length} fields)
                      </span>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => handleEditExtractionTemplate(item)}
                          disabled={loading}
                          className="rounded border border-slate-300 px-2 py-0.5 text-[11px] font-medium text-slate-700 hover:border-slate-400 disabled:opacity-60"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDuplicateExtractionTemplate(item)}
                          disabled={loading}
                          className="rounded border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-700 hover:border-indigo-300 disabled:opacity-60"
                        >
                          Duplicate
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteExtractionTemplate(item.documentExtractionTemplateId, item.extractionTemplateName)}
                          disabled={loading}
                          className="rounded border border-rose-200 bg-rose-50 px-2 py-0.5 text-[11px] font-medium text-rose-700 hover:border-rose-300 disabled:opacity-60"
                        >
                          Delete
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {showExtractionTrainer && extractionPreview && (() => {
        const trainerUsesSpreadsheetView = isSpreadsheetTrainingFile(extractionTestFile);
        const spreadsheetSheets = trainerUsesSpreadsheetView
          ? parseSpreadsheetPreviewText(extractionPreview.extractedText ?? '')
          : [];
        return (
        <div className="fixed inset-0 z-50 bg-slate-900/45 backdrop-blur-sm p-0 sm:p-3 lg:p-6">
          <div className="mx-auto flex h-full w-full max-w-[96vw] flex-col rounded-none border border-slate-200 bg-white p-3 sm:rounded-xl sm:p-4 lg:p-5">
            <div className="mb-3 flex items-center justify-between border-b border-slate-200 pb-3">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Entity Extraction Trainer</h3>
                <p className="text-xs text-slate-500">
                  {trainerUsesSpreadsheetView
                    ? 'Select text in the extracted spreadsheet preview. Drag over cells to capture row + column context (e.g. kb_unit_1__may_26 = 200).'
                    : 'Highlight text directly on the PDF preview and let AI generate editable field/value mappings.'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowExtractionTrainer(false)}
                className="rounded-md border border-slate-300 px-3 py-1 text-sm text-slate-700"
              >
                Close
              </button>
            </div>
            <div className="mb-3 flex items-center gap-2 lg:hidden">
              <button
                type="button"
                onClick={() => setMobileTrainerView('document')}
                className={`rounded-md px-3 py-1.5 text-xs font-semibold ${
                  mobileTrainerView === 'document'
                    ? 'bg-slate-900 text-white'
                    : 'border border-slate-300 bg-white text-slate-700'
                }`}
              >
                {trainerUsesSpreadsheetView ? 'Spreadsheet' : 'PDF'}
              </button>
              <button
                type="button"
                onClick={() => setMobileTrainerView('fields')}
                className={`rounded-md px-3 py-1.5 text-xs font-semibold ${
                  mobileTrainerView === 'fields'
                    ? 'bg-slate-900 text-white'
                    : 'border border-slate-300 bg-white text-slate-700'
                }`}
              >
                Fields
              </button>
            </div>
            <div className="grid min-h-0 flex-1 gap-4 lg:grid-cols-[minmax(0,1fr)_380px]">
              <div className={`${mobileTrainerView === 'document' ? 'flex' : 'hidden'} min-h-0 flex-col rounded-lg border border-slate-200 bg-slate-50 p-3 lg:flex`}>
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {trainerUsesSpreadsheetView
                      ? 'Uploaded spreadsheet - drag cells to capture row + column'
                      : 'Uploaded PDF - highlight directly on document'}
                  </p>
                  {!trainerUsesSpreadsheetView && (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setPdfScale((prev) => Math.max(1, Number((prev - 0.15).toFixed(2))))}
                      className="rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700"
                    >
                      Zoom -
                    </button>
                    <button
                      type="button"
                      onClick={() => setPdfScale((prev) => Math.min(2.6, Number((prev + 0.15).toFixed(2))))}
                      className="rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700"
                    >
                      Zoom +
                    </button>
                    <span className="text-xs text-slate-500">{Math.round(pdfScale * 100)}%</span>
                  </div>
                  )}
                </div>
                <div
                  ref={pdfSelectionContainerRef}
                  className="relative min-h-0 flex-1 cursor-crosshair overflow-auto rounded border border-slate-300 bg-white p-2"
                  onMouseDown={handleDragSelectionStart}
                  onMouseMove={handleDragSelectionMove}
                  onMouseUp={handleDragSelectionEnd}
                  onMouseLeave={handleDragSelectionEnd}
                >
                  {trainerUsesSpreadsheetView ? (
                    spreadsheetSheets.length > 0 ? (
                      <div className="space-y-4 pb-2">
                        {spreadsheetSheets.map((sheet) => (
                          <div key={sheet.name}>
                            <p className="mb-1 text-xs font-semibold text-slate-600">{sheet.name}</p>
                            <table className="w-full min-w-max border-collapse text-xs text-slate-800">
                              <tbody>
                                {sheet.rows.map((row, rowIndex) => (
                                  <tr key={`${sheet.name}-${rowIndex}`}>
                                    {row.map((cell, cellIndex) => {
                                      const rowLabel = getSpreadsheetRowLabel(
                                        row,
                                        rowIndex,
                                        sheet.headerRowIndex
                                      );
                                      const colHeader = getSpreadsheetColHeader(sheet.columnHeaders, cellIndex);
                                      return (
                                      <td
                                        key={`${sheet.name}-${rowIndex}-${cellIndex}`}
                                        data-trainer-selectable
                                        data-trainer-cell="true"
                                        data-sheet-name={sheet.name}
                                        data-row-index={rowIndex}
                                        data-col-index={cellIndex}
                                        data-row-label={rowLabel}
                                        data-col-header={colHeader}
                                        data-cell-value={cell}
                                        className="border border-slate-200 px-2 py-1 align-top select-text"
                                      >
                                        {cell}
                                      </td>
                                      );
                                    })}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ))}
                      </div>
                    ) : extractionPreview.extractedText?.trim() ? (
                      <pre className="whitespace-pre-wrap break-words font-mono text-xs leading-5 text-slate-800 select-text">
                        {extractionPreview.extractedText}
                      </pre>
                    ) : (
                      <div className="rounded border border-dashed border-slate-300 bg-white p-4 text-xs text-slate-500">
                        No text could be extracted from this spreadsheet.
                      </div>
                    )
                  ) : extractionTestFile ? (
                    <Document
                      file={extractionTestFile}
                      onLoadSuccess={({ numPages }) => {
                        setPdfPageCount(numPages);
                        setPdfLoadError(null);
                      }}
                      onLoadError={(error) => {
                        setPdfLoadError(error?.message || 'Failed to load PDF file.');
                        setPdfPageCount(0);
                      }}
                      className="flex flex-col items-center gap-3"
                    >
                      {Array.from({ length: pdfPageCount }, (_, pageIndex) => (
                        <div key={`page-${pageIndex + 1}`} className="shadow-sm">
                          <Page
                            pageNumber={pageIndex + 1}
                            scale={pdfScale}
                            renderAnnotationLayer
                            renderTextLayer
                          />
                        </div>
                      ))}
                    </Document>
                  ) : (
                    <div className="rounded border border-dashed border-slate-300 bg-white p-4 text-xs text-slate-500">
                      PDF preview is unavailable for this file.
                    </div>
                  )}

                  {!trainerUsesSpreadsheetView && pdfLoadError && (
                    <div className="mt-2 rounded border border-rose-200 bg-rose-50 p-2 text-xs text-rose-700">
                      {pdfLoadError}
                    </div>
                  )}

                  {dragPreviewRect && (
                    <div
                      className="pointer-events-none absolute rounded border-2 border-dashed border-indigo-500 bg-indigo-200/25"
                      style={{
                        left: dragPreviewRect.left,
                        top: dragPreviewRect.top,
                        width: dragPreviewRect.width,
                        height: dragPreviewRect.height,
                      }}
                    />
                  )}

                  {trainerHighlights.map((highlight) =>
                    highlight.rects.map((rect, idx) => (
                      <div
                        key={`${highlight.id}-${idx}`}
                        className="pointer-events-none absolute rounded-sm border"
                        style={{
                          left: rect.left,
                          top: rect.top,
                          width: rect.width,
                          height: rect.height,
                          borderColor: highlight.color,
                          backgroundColor: `${highlight.color}55`,
                        }}
                        title={highlight.selectedText}
                      />
                    ))
                  )}
                </div>
              </div>
              <div className={`${mobileTrainerView === 'fields' ? 'flex' : 'hidden'} min-h-0 flex-col space-y-3 rounded-lg border border-slate-200 p-3 lg:flex`}>
                {suggestedExtractionFields.length > 0 && (
                  <div className="rounded-lg border border-indigo-100 bg-indigo-50 p-2">
                    <p className="text-xs font-semibold text-indigo-800">AI Suggested Fields</p>
                    <div className="mt-2 max-h-[30vh] space-y-1 overflow-y-auto pr-1">
                      {suggestedExtractionFields.map((field, idx) => (
                        <div key={`${field.fieldName}-${idx}`} className="rounded border border-indigo-200 bg-white p-2 text-xs">
                          <input
                            value={field.fieldName}
                            onChange={(event) =>
                              setSuggestedExtractionFields((prev) =>
                                prev.map((item, index) =>
                                  index === idx ? { ...item, fieldName: event.target.value } : item
                                )
                              )
                            }
                            className="w-full rounded border border-slate-300 px-1.5 py-1 font-medium text-slate-800"
                          />
                          <textarea
                            value={field.exampleValue}
                            onChange={(event) =>
                              setSuggestedExtractionFields((prev) =>
                                prev.map((item, index) =>
                                  index === idx ? { ...item, exampleValue: event.target.value } : item
                                )
                              )
                            }
                            className="mt-1 min-h-12 w-full rounded border border-slate-300 px-1.5 py-1 text-slate-600"
                          />
                          <button
                            type="button"
                            onClick={() => addSuggestedField(field)}
                            className="mt-1 rounded border border-slate-300 px-2 py-0.5 text-[11px] font-medium text-slate-700 hover:border-slate-400"
                          >
                            Add
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <button
                  type="button"
                  onClick={captureSelectedText}
                  disabled={selectionLoading}
                  className="inline-flex w-full items-center justify-center rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {selectionLoading ? 'Analysing selection...' : 'Capture Selected Text'}
                </button>
                <p className="text-xs text-slate-500">
                  {trainerUsesSpreadsheetView
                    ? 'Drag over one or more data cells. Field names combine the row label and column header (editable below).'
                    : 'Drag a box over the PDF like a screenshot tool. AI will infer field name/value pairs and keep a colored highlight.'}
                </p>
                <div className="min-h-0 flex-1 space-y-2 overflow-y-auto rounded border border-slate-200 bg-slate-50 p-2">
                  <p className="text-xs font-semibold text-slate-700">Field List (editable)</p>
                  {stagedExtractionFields.length === 0 ? (
                    <p className="text-xs text-slate-500">No fields captured yet.</p>
                  ) : (
                    stagedExtractionFields.map((field) => (
                      <div key={field.id} className="rounded border border-slate-200 bg-white p-2">
                        <label className="block text-[11px] font-medium text-slate-600">Field Name</label>
                        <input
                          value={field.fieldName}
                          onChange={(event) => updateStagedField(field.id, { fieldName: event.target.value })}
                          className="mt-1 w-full rounded border border-slate-300 px-2 py-1 text-xs"
                        />
                        <label className="mt-2 block text-[11px] font-medium text-slate-600">Example Value</label>
                        <textarea
                          value={field.exampleValue}
                          onChange={(event) => updateStagedField(field.id, { exampleValue: event.target.value })}
                          className="mt-1 min-h-14 w-full rounded border border-slate-300 px-2 py-1 text-xs"
                        />
                        <button
                          type="button"
                          onClick={() => removeStagedField(field.id)}
                          className="mt-1 rounded border border-rose-200 bg-rose-50 px-2 py-0.5 text-[11px] font-medium text-rose-700"
                        >
                          Remove
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        );
      })()}
    </div>
  );
};

export default DocumentHub;
